import { flatMap, uniqBy } from 'lodash'
import { commonInitializationService } from '@/api/resources/common/initialization/common-initialization.service'
import priorityResource from '@/api/resources/init/PriorityResource'
import IdentifierRegexesResource from '@/api/resources/IdentifierRegexesService'
import { MODULES } from '@/api/resources/ModuleResource'
import { store } from '@/store/store'
import SockJS from 'sockjs-client'
import Stomp from 'webstomp-client'
import anapathPrinterRestClient from '@/modules/printModule/services/AnapathPrinterRestClient'
import reportModelConfigurationsService from '@/modules/printModule/services/ReportModelConfigurationsService'
import { MODULE_OPERATION_CONFIGURATION_PREFIX, ModuleOperation } from '@/api/operations/models/module-operation'
import { commonSampleClassificationService } from '@/api/resources/common/sample-classification/sample-classification.service'
import { uiComponentCustomConfigService } from '@/api/services/common/ui-component/custom-config/ui-component-custom-config.service'
import { DEFAULT_LOCALE } from '@/shared/services/services-constants'
import { loadLanguageAsync } from '@/shared/util/i18n.helper'
import { PromiseUtils } from '@/shared/util/promise-utils'
import { StoreUtils } from '@/shared/util/store-utils'
import { UiComponentUtils } from '@/shared/util/ui-component-utils'
import { initialRootStoreState } from '@/store/root/root.store'
import { WEBSOCKET_BASE_URL } from '@/api/api'
import { UserType } from '@/api/resources/common/initialization/common-initialization.dto.ts'

class SessionStore {
    constructor() {
        this.state = this.getDefaultState()
    }
    reset() {
        if (this.state.stompClient) {
            try {
                this.state.stompClient.disconnect()
            } catch (e) {
                console.error(e)
            }
        }
        this.state = this.getDefaultState()
    }
    getDefaultState() {
        return {
            token: null,
            expires: null,
            passwordSingleUse: null,
            userDowntimeInSeconds: null,
            selectedLaboratoryConfiguration: null,
            printers: [],
            laboratoryMap: {},
            flatOperationCodes: [],
            priorities: [],
            stompClient: null,
            userDefaultAccessUiComponentCode: 'uicomponent.userapp.defaultaccess'
        }
    }
    setToken(value) {
        this.state.token = value
    }
    setExpires(value) {
        this.state.expires = value
    }
    setPasswordSingleUse(value) {
        this.state.passwordSingleUse = value
    }
    setUserDowntimeInSeconds(value) {
        this.state.userDowntimeInSeconds = value
    }
    setCountdownToCloseSessionInSeconds(value) {
        this.state.countdownToCloseSessionInSeconds = value
    }
    async update() {
        if (!store.state.userLoggedIn) {
            this.reset()
        } else {
            await Promise.all([
                this.loadGlobalParameters(),
                this.updateLaboratoriesConfiguration(),
                this.updatePriorities()
            ])

            await this.updateUserInformation()
            await Promise.all([
                this.loadUserTranslations(),
                this.loadUserDefaultAccess(),
                this.loadRegexes()
            ])
            this.createWebsocketConnectionIfNotSet()
            this.checkPrintServiceStatus()
        }
    }
    async loadUserTranslations() {
        const uiLanguage = store.state.userInformation?.uiLanguage
        if (uiLanguage) {
            await loadLanguageAsync(uiLanguage.locale, uiLanguage.id)
        }
    }
    async loadGlobalParameters() {
        const globalParameters = await commonInitializationService.getGlobalParameters()
        store.commit('updateGlobalParameters', globalParameters)
    }
    getUserDefaultLabId() {
        return store.state.userInformation?.personnel?.defaultLaboratory?.labId ?? null
    }
    getUserPersonnelId() {
        return store.state.userInformation?.personnel?.id ?? null
    }
    mapUserDefaultAccessModuleToRequest(labModule) {
        return {
            laboratory: labModule.laboratory,
            moduleCode: labModule.moduleCode,
            label: labModule.label
        }
    }
    async saveUserDefaultAccess(userDefaultAccessUiComponentConfig) {
        let config
        if (this.isPersonnelUser()) {
            config = {
                defaultLabModules: userDefaultAccessUiComponentConfig.defaultLabModules.map(labModule => this.mapUserDefaultAccessModuleToRequest(labModule)),
                defaultLab: userDefaultAccessUiComponentConfig.defaultLab
            }
        }

        let userDefaultAccessUiComponent = null
        if (config) {
            userDefaultAccessUiComponent =
                await UiComponentUtils.saveCustomConfig(
                    this.state.userDefaultAccessUiComponentCode,
                    { config })
            if (userDefaultAccessUiComponent) {
                store.commit('updateDefaultAccess', userDefaultAccessUiComponent.config)
            }
        }
        return userDefaultAccessUiComponent
    }
    mapDefaultLabModules(laboratoriesConfiguration) {
        return laboratoriesConfiguration.map(laboratoryConfiguration => ({
            laboratory: laboratoryConfiguration.laboratory,
            moduleCode: MODULES[ModuleOperation.DASHBOARD].moduleCode,
            id: MODULES[ModuleOperation.DASHBOARD].moduleCode,
            label: MODULES[ModuleOperation.DASHBOARD].getLabel()
        }))
    }
    async loadPersonnelUserDefaultAccess(userDefaultAccessUiComponent) {
        if (!userDefaultAccessUiComponent?.config?.defaultLab?.labId) {
            const userDefaultLabId = this.getUserDefaultLabId()
            const labConf =
                store.state.laboratoriesConfiguration.find(
                    laboratoryConfiguration => laboratoryConfiguration.laboratory.labId === userDefaultLabId)
            const userDefaultAccessUiComponentConfig = {
                defaultLab: labConf?.laboratory,
                defaultLabModules: this.mapDefaultLabModules(store.state.laboratoriesConfiguration)
            }
            await this.saveUserDefaultAccess(userDefaultAccessUiComponentConfig)
            this.updateLabId(userDefaultLabId)
        } else {
            store.commit('updateDefaultAccess', userDefaultAccessUiComponent.config)

            const userDefaultLabId = userDefaultAccessUiComponent.config.defaultLab.labId
            if (this.existsLaboratoryInSessionStorage()) {
                this.updateLabId(this.getLaboratoryInSessionStorage())
            } else if (store.state.labId === initialRootStoreState().labId) {
                this.updateLabId(userDefaultLabId)
            }
        }
    }
    async loadUserDefaultAccess() {
        const userDefaultAccessUiComponent =
            await PromiseUtils.toCallSecure(
                uiComponentCustomConfigService.get(this.state.userDefaultAccessUiComponentCode),
                error => PromiseUtils.handleAxiosError(error, [404]))
        if (this.isPersonnelUser()) {
            await this.loadPersonnelUserDefaultAccess(userDefaultAccessUiComponent)
        } else {
            this.updateLabId(this.existsLaboratoryInSessionStorage() ? this.getLaboratoryInSessionStorage() : this.getUserDefaultLabId())
        }
    }
    isPersonnelUser() {
        return store.state.userInformation?.userType === UserType.PERSONNEL
    }
    isPetitionerUser() {
        return store.state.userInformation?.userType === UserType.PETITIONER
    }
    getLaboratoryInSessionStorage() {
        return sessionStorage.getItem('labId') && Number(sessionStorage.getItem('labId'))
    }
    existsLaboratoryInSessionStorage() {
        return !!this.getLaboratoryInSessionStorage() && !Number.isNaN(this.getLaboratoryInSessionStorage())
    }
    updateLabId(labId) {
        sessionStorage.setItem('labId', labId)
        store.commit('updateLabId', labId)
    }
    async updateUserInformation() {
        const userInformation = await commonInitializationService.getUserInformation()
        store.commit('updateUserInformation', userInformation)
    }
    getLocale() {
        // TODO: default language should be taken from somewhere else and not hardcoded
        return store.state.userInformation?.uiLanguage.locale ?? DEFAULT_LOCALE
    }
    async updateLaboratoriesConfiguration() {
        const laboratoriesConfiguration = await commonInitializationService.getLaboratoriesConfiguration()
        store.commit('updateLaboratoriesConfiguration', laboratoriesConfiguration)

        this.state.flatOperationCodes = this.flattenOperations(laboratoriesConfiguration).map(i => i.code)
        this.state.laboratoryMap = laboratoriesConfiguration.reduce((laboratoryMap, labConfig) => {
            const laboratory = labConfig.laboratory
            laboratoryMap[laboratory.labId] = laboratory
            return laboratoryMap
        }, {})
    }
    async loadRegexes() {
        let regexes
        if (this.isPetitionerUser()) {
            regexes = await IdentifierRegexesResource.getClinicalRegexes()
        } else {
            regexes = await commonInitializationService.getIdentifierRegexes()
        }
        store.commit('updateRegex', regexes ?? [])
    }
    async getLaboratoryParams(labId) {
        const configuration = {}
        if (labId) {
            const laboratoryParams = await commonSampleClassificationService.getCommonParametersLaboratorySampleClassifications(labId)
            laboratoryParams.forEach((param) => {
                if (param.parameter.code === 'configuration.parameter.laboratory.samples.procedure.supported') {
                    configuration.showProcedure = param.value === 'true'
                }
                if (param.parameter.code === 'configuration.parameter.laboratory.samples.mostlikelydiagnosis.supported') {
                    configuration.showMostlikelydiagnosis = param.value === 'true'
                }
            })
        }
        store.commit('updateOntologyParametersConfiguration', configuration)
    }
    flattenOperations(operations) {
        const flattened = flatMap(operations, i => i.operations)
        return uniqBy(flattened, i => i.code)
    }
    async updatePriorities() {
        this.state.priorities = await priorityResource.index()
    }
    hasLabOperation(labId, operationCode) {
        const lab = StoreUtils.laboratories.find(laboratory => laboratory.labId === labId)
        return !!lab?.operationCodes.includes(operationCode)
    }
    hasLabOperationOnSelectedLaboratory(operationCode) {
        return !!StoreUtils.selectedLaboratoryConfiguration?.operations.find(
            operation => operation.code === operationCode)
    }
    hasOperationInAnyLaboratory(operationCode) {
        return !!this.state.flatOperationCodes.find(operation => operation === operationCode)
    }
    hasAnyConfigurationOperation() {
        return !!StoreUtils.selectedLaboratoryConfiguration?.operations.find(
            operation => operation.code.startsWith(MODULE_OPERATION_CONFIGURATION_PREFIX))
    }
    hasAnyControlPanelOperation() {
        return this.state.flatOperationCodes.includes(
            ModuleOperation.STATISTICS_PANEL_ACCESS,
            ModuleOperation.SCIENTIFIC_PANEL_ACCESS,
            ModuleOperation.REPORTS_PANEL)
    }

    createWebsocketConnectionIfNotSet() {
        if (!this.state.stompClient) {
            const token = this.state.token
            if (token) {
                const socket = new SockJS(WEBSOCKET_BASE_URL + '/main/?authenticationToken=' + token)
                const stompClient = Stomp.over(socket, {
                    debug: false,
                    heartbeat: { outgoing: 120000, incoming: 120000 }
                })
                try {
                    stompClient.connect(
                        {},
                        () => {
                            this.state.stompClient = stompClient
                        }, error => {
                            console.error(error)
                            stompClient.disconnect(() => {
                                this.state.stompClient = null
                                setTimeout(() => this.createWebsocketConnectionIfNotSet(), 10000)
                            })
                        }
                    )
                } catch (e) {
                    console.error(e)
                }
            }
        }
    }

    async checkPrintServiceStatus(updatePrintDevices = true) {
        let status = false
        try {
            status = await anapathPrinterRestClient.getPrintServiceStatus()
        } catch (e) {
            status = false
        } finally {
            store.commit('updatePrintServiceStatus', status)
        }

        if (updatePrintDevices && status) {
            const devices = await reportModelConfigurationsService.getConfiguredPrinters()
            let reportModels = await reportModelConfigurationsService.getConfiguredReportModels()
            reportModels = reportModelConfigurationsService.matchConfigurations(reportModels, devices)
            store.commit('updatePrintDevices', reportModels)
        }
    }
}

export default new SessionStore()
